import { useEffect, useRef } from 'react';
import { conversationBus } from '@/lib/conversationBus';
import type { ConversationEventMap } from '@/lib/conversationBus';

/**
 * Hook to easily subscribe to ConversationBus events tailored for a specific conversation.
 *
 * @param event The event name to listen to
 * @param conversationId The ID of the conversation to filter by (if null/undefined, it listens to all)
 * @param callback The function to call when the event is emitted
 */
export function useConversationBus<K extends keyof ConversationEventMap>(
  event: K,
  conversationId: number | null | undefined,
  callback: (data: ConversationEventMap[K]) => void,
)
{
  const callbackRef = useRef(callback);

  // Update ref when callback changes, no need to re-subscribe
  useEffect(() =>
  {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() =>
  {
    const unsubscribe = conversationBus.on(event, ((data: any) =>
    {
      if (conversationId == null || data.conversationId === conversationId)
      {
        callbackRef.current(data);
      }
    }) as any);

    return () =>
    {
      unsubscribe();
    };
  }, [event, conversationId]);
}
